import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components'
import toast from 'react-hot-toast';
import Api from '../../../utils/Api';
import ShareWithGroup from './ShareWithGroup';
import ShareWithUser from '../../../components/ShareWithUser';
import ConfirmButton from '../../../components/ConfirmButton';
import UserCard from '../../../components/UserCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake, faHandshakeSlash } from '@fortawesome/free-solid-svg-icons';

const sharedWithUserStyles = css`
display: flex;
gap: 5px;
align-items: center;
position: relative;

.buttons {
  display: flex;
  gap:5px;
  position: absolute;
  top:5px;
  right: 5px;
}
.remove {
  font-size: .75em;
}

`
const SharedWithUser = styled(({ className, isCollaborator = false, user, onSetCollaborate, onUnshare }) => {

  return (
    <div className={className}>
      < div className='buttons'>
        {isCollaborator
          ? <ConfirmButton
            text={`Do you want to stop collaborating with ${user.firstname} ${user.lastname} on this list?`}
            confirm='Yes'
            cancel='No'
            className='remove danger'
            onConfirm={() => onSetCollaborate({ userId: user._id }, false)}
            onCancel={() => { }}>
            <FontAwesomeIcon icon={faHandshakeSlash} />
          </ConfirmButton>
          : <ConfirmButton
            text={`Do you want to allow ${user.firstname} ${user.lastname} to collaborate on this list?`}
            confirm='Yes'
            cancel='No'
            className='remove primary'
            onConfirm={() => onSetCollaborate({ userId: user._id }, true)}
            onCancel={() => { }}>
            <FontAwesomeIcon icon={faHandshake} />
          </ConfirmButton>
        }
        <ConfirmButton
          text={`Do you want to remove access for ${user.firstname} ${user.lastname}`}
          confirm='Yes'
          cancel='No'
          className='remove danger'
          onConfirm={() => onUnshare({ userId: user._id })}
          onCancel={() => { }}>
          X
        </ConfirmButton>
      </div>
      <UserCard user={user} />
    </div>
  )
})`${sharedWithUserStyles}`;

const styles = css`
display: flex;
column-gap: 50px;
flex-wrap: wrap;
justify-content: center;
align-content: flex-start;
width: 100%;

  & .section {
    display: flex;
    flex-direction: column;
    gap: 0px;
    max-width: 400px;
    width: 100%;

    > .title {
      padding-left: 3px;
      padding-bottom: 3px;
      border-bottom: 1px solid gray;
      font-weight: bold;
    }
    & .content {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding:5px;

      & .user-list {
        display: flex;
        flex-direction: column;
        gap: 3px;
      }
    }
  }
  & .empty {
    font-style: italic;
  }
`
const ListsShare = styled(({ className }) => {
  const [list, setList] = useState(null);
  const [myGroups, setMyGroups] = useState([]);
  const [friendsGroups, setFriendsGroups] = useState([]);
  const [, setIsLoading] = useState(false);
  const { id } = useParams();

  const groupList = useMemo(() => {
    let groupList = [];
    if (list) {
      groupList = [...myGroups, ...friendsGroups].map((group, i) => {
        return ({
          ...group,
          isShared: list.sharedWith.groups.find(_group => group._id === _group._id),
          isCollaborating: list.collaborateWith.groups.find(_group => group._id === _group._id),
        })
      });
    }
    return groupList;
  }, [friendsGroups, list, myGroups]);

  const fetchList = useCallback(async () => {
    const [error, list] = await Api.getList(id);
    if (error) {
      console.error(error);
      return toast.error(Api.error(error));
    }
    setList(list);
  }, [id]);

  const fetchMyGroups = useCallback(async () => {
    const [error, myGroups] = await Api.getMyGroups();
    if (error) return toast.error(Api.error(error));
    setMyGroups(myGroups);
  }, []);

  const fetchMyFriendsGroups = useCallback(async () => {
    const [error, friendsGroups] = await Api.getFriendsGroups();
    if (error) return toast.error(Api.error(error));
    setFriendsGroups(friendsGroups);
  }, []);

  const shareWithGroup = async ({ groupId }) => {
    const [error,] = await Api.shareList({ groupId, listId: list._id });
    if (error) {
      console.error(error);
      toast.error(Api.error(error));
    } else {
      load();
    }
  }

  const unshareList = async ({ groupId, userId }) => {
    const [error,] = await Api.unshareList({ groupId, userId, listId: list._id });
    if (error) {
      console.error(error);
      toast.error(Api.error(error));
    } else {
      load();
    }
  }

  const setCollaborate = async ({ groupId, userId }, allow) => {
    const [error,] = await Api.setListCollaboration({ groupId, userId, listId: list._id, allow });
    if (error) {
      console.error(error);
      toast.error(Api.error(error));
    } else {
      load();
    }
  }

  const load = useCallback(async () => {
    setIsLoading(true);
    await fetchList();
    await fetchMyGroups();
    await fetchMyFriendsGroups();
    setIsLoading(false);
  }, [fetchList, fetchMyGroups, fetchMyFriendsGroups]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      {list ?
        <div className={className}>
          <div className='section'>
            <div className='title'>Users</div>
            <div className='content'>
              <div className='user-list'>
                {
                  list.sharedWith.users.length
                    ? list.sharedWith.users.map((user, i) => {
                      const isCollaborator = list.collaborateWith.users.find(cUser => cUser._id === user._id) ? true : false;
                      return <SharedWithUser
                        key={i}
                        user={user}
                        isCollaborator={isCollaborator}
                        onSetCollaborate={setCollaborate}
                        onUnshare={unshareList} />
                    })
                    : <span className='empty'>Not shared with any users</span>
                }
              </div>
              <ShareWithUser listId={list._id} onShare={load} />
            </div>
          </div>
          <div className='section'>
            <div className='title'>Groups</div>
            <div className='content'>
              {groupList.map((group, i) => <ShareWithGroup
                key={i}
                list={list}
                group={group}
                onUnshare={unshareList}
                onShare={shareWithGroup}
                onSetCollaborate={setCollaborate} />)}
            </div>
          </div>
        </div>
        : <div>
          No list is available for sharing
        </div>
      }
    </>
  )
})`${styles}`;

export default ListsShare;
